/**
 * followupClient — fetch personalized follow-up chips for a thread tail.
 *
 * Thin authed client for the getThreadFollowupSuggestions Cloud Function
 * (fetch idiom mirrors onboardingRecsPrefetch). The server caches per
 * (user, thread, tail-hash); this client adds an in-memory layer keyed the
 * same way so one tab never re-requests the tail it already resolved, and
 * concurrent callers share one in-flight promise instead of double-billing.
 *
 * Failures resolve to an empty PROVISIONAL result — chips are enhancement,
 * never an error surface — and provisional answers are never pinned for the
 * tail (owner directive 2026-08-30, the canned-chips repetition bug).
 */

import { getAuth } from 'firebase/auth';

export interface FollowupTailMessage {
  role: 'user' | 'assistant' | 'other';
  name?: string;
  text: string;
}

function getProjectId(): string {
  return process.env.REACT_APP_FIREBASE_PROJECT_ID || 'tensile-tenure-453409-k3';
}

/** Deterministic, cheap key for a sanitized tail (djb2 over the serialized tail). */
export function computeTailKey(threadId: string, tail: ReadonlyArray<FollowupTailMessage>): string {
  const serialized = `${threadId}\u0000${tail.map((m) => `${m.role}:${m.text}`).join('\u0000')}`;
  let hash = 5381;
  for (let i = 0; i < serialized.length; i += 1) {
    hash = ((hash << 5) + hash + serialized.charCodeAt(i)) | 0; // eslint-disable-line no-bitwise
  }
  return `${(hash >>> 0).toString(36)}:${serialized.length}`; // eslint-disable-line no-bitwise
}

/**
 * What a fetch resolves to. `provisional` marks a non-final answer — another
 * request holds the generation lease (`generating`), the server served its
 * deterministic floor (`degraded`), or the round-trip produced nothing. The
 * hook renders provisional chips (the row must always show, 2026-08-23) but
 * never pins them as the tail's resolution — that pinning was one half of the
 * canned-chips repetition bug (owner directive 2026-08-30).
 */
export interface FollowupFetchResult {
  suggestions: string[];
  provisional: boolean;
  /** Which generation stage produced these (2026-09-02). 'fast' is the set
   *  generated in parallel with the answer — real chips, but produced without
   *  having seen it; only 'upgraded' is terminal. Absent on floors/failures. */
  stage?: 'fast' | 'upgraded';
}

const cache = new Map<string, Promise<FollowupFetchResult>>();
/** Bounded so a long session can't grow the map without limit. */
const CACHE_MAX_ENTRIES = 200;

export function clearFollowupCacheForTests(): void {
  cache.clear();
}

/**
 * Drop a cached promise once the current microtask settles — the in-flight
 * dedupe still holds for concurrent callers, but the result is not remembered
 * for the life of the tail. Used for every non-answer (transport failure, a
 * lease loss, an empty set) so a later attempt can try again.
 */
function evictSoon(key: string): void {
  setTimeout(() => { cache.delete(key); }, 0);
}

export async function fetchThreadFollowupSuggestions(args: {
  threadId: string;
  tail: ReadonlyArray<FollowupTailMessage>;
  /**
   * STARTER mode (2026-09-02): chips for a thread with no conversation yet.
   * An empty tail is legitimate here and the server generates from the account
   * picture instead. Keyed per MODE, not per thread — starters are a property
   * of the person right now, so every new thread inside the cache window is an
   * instant hit rather than a fresh generation.
   */
  starter?: boolean;
}): Promise<FollowupFetchResult> {
  const threadId = (args.threadId || '').trim();
  const tail = Array.isArray(args.tail) ? args.tail : [];
  if (!threadId || (tail.length === 0 && !args.starter)) {
    return { suggestions: [], provisional: true };
  }

  const key = args.starter ? 'starter' : computeTailKey(threadId, tail);
  const existing = cache.get(key);
  if (existing) return existing;

  const request = (async (): Promise<FollowupFetchResult> => {
    try {
      const user = getAuth().currentUser;
      if (!user) return { suggestions: [], provisional: true };
      const token = await user.getIdToken();
      const response = await fetch(
        `https://us-central1-${getProjectId()}.cloudfunctions.net/getThreadFollowupSuggestions`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            threadId,
            conversationTail: tail,
            ...(args.starter ? { mode: 'starter' } : {}),
          }),
        },
      );
      if (!response.ok) {
        evictSoon(key);
        return { suggestions: [], provisional: true };
      }
      const data = (await response.json().catch(() => null)) as
        | { suggestions?: unknown; generating?: boolean; degraded?: boolean; stage?: unknown }
        | null;
      // R17 single-flight: another tab/device holds the generation lease and
      // we got its (possibly stale) chips. Don't pin this answer for the tail
      // forever — evict so a later gate re-open picks up the winner's cached
      // result without a second distillation (server cache hit). Degraded
      // answers (the server's deterministic floor) are likewise never pinned
      // (owner directive 2026-08-30) — a retry past the server's lease window
      // regenerates for real.
      const stage = data?.stage === 'fast' || data?.stage === 'upgraded' ? data.stage : undefined;
      // A FAST set is real, contextual and worth showing immediately — but it
      // was generated without the answer, so it is not the tail's final word:
      // stay provisional so the ladder keeps running and the upgraded set can
      // still arrive and take the lead (2026-09-02).
      const provisionalReason = data?.generating === true
        || data?.degraded === true
        || stage === 'fast';
      if (provisionalReason) evictSoon(key);
      const suggestions = Array.isArray(data?.suggestions)
        ? (data!.suggestions as unknown[]).filter((s): s is string => typeof s === 'string' && s.trim().length > 0)
        : [];
      // Owner directive 2026-08-23 (the row must always render): an EMPTY
      // result is never a resolution worth remembering. Pinning it made one
      // hiccup — a transport blip, a degenerate model output — hide the row
      // for the rest of that tail's life. Evicting lets the caller's retry
      // reach the server's own deterministic floor.
      if (suggestions.length === 0) evictSoon(key);
      return {
        suggestions,
        provisional: provisionalReason || suggestions.length === 0,
        ...(stage ? { stage } : {}),
      };
    } catch {
      evictSoon(key);
      return { suggestions: [], provisional: true };
    }
  })();

  // Cache the promise (in-flight dedupe); evict oldest insertion on overflow.
  if (cache.size >= CACHE_MAX_ENTRIES) {
    const oldest = cache.keys().next().value;
    if (oldest !== undefined) cache.delete(oldest);
  }
  cache.set(key, request);
  return request;
}
